import type {
    CreditPackageResource,
    CreditTransactionResource,
    CreditWalletResource,
    PaymentResource,
    SubscriptionOverviewResource,
} from './types';

/**
 * Tegoed, zoals de schermen het zien.
 *
 * Elk bedrijf heeft één wallet met een **alleen-toevoegen grootboek**: het saldo
 * is de som van de boekingen en corrigeren gebeurt met een tegenboeking. Tegoed
 * telt in hele credits, nooit in euro's (`docs/woordenlijst.md`); alleen een
 * tegoedpakket heeft een prijs.
 */

/* -------------------------------------------------------------------------- */
/* Wallet                                                                      */
/* -------------------------------------------------------------------------- */

export interface Wallet {
    /** Al netto: gereserveerde credits zijn er al af. */
    balance: number;
    /** Vastgezet voor keuringen die nog lopen; zit al in `balance` verrekend. */
    reserved: number;
    /** Wat je nog kunt uitgeven: saldo plus de ruimte onder nul. */
    spendable: number;
    /** `0` is: geen waarschuwing. */
    lowBalanceThreshold: number;
    isLow: boolean;
    /** Bij een leeg tegoed geen nieuwe keuringen meer. */
    blockOnEmpty: boolean;
    /** Hoe ver het saldo onder nul mag voordat er geblokkeerd wordt. */
    graceCredits: number;
    /** Inbegrepen keuringen van deze abonnementsmaand: vervallen aan het eind ervan. */
    includedBalance: number;
    /** Gekocht of gekregen: vervalt nooit. */
    purchasedBalance: number;
    includedPeriodEndsAt: string | null;
}

export function toWallet(resource: CreditWalletResource): Wallet {
    return {
        balance: resource.balance,
        reserved: resource.reserved,
        spendable: resource.spendable,
        includedBalance: resource.included_balance ?? 0,
        purchasedBalance: resource.purchased_balance ?? resource.balance,
        includedPeriodEndsAt: resource.included_period_ends_at ?? null,
        lowBalanceThreshold: resource.low_balance_threshold,
        isLow: resource.is_low,
        blockOnEmpty: resource.block_on_empty,
        graceCredits: resource.grace_credits,
    };
}

/* -------------------------------------------------------------------------- */
/* Grootboek                                                                   */
/* -------------------------------------------------------------------------- */

export const CREDIT_TRANSACTION_TYPES = [
    'topup',
    'reservation',
    'consumption',
    'release',
    'refund',
    'adjustment',
    'bonus',
    'expiry',
] as const;

export type CreditTransactionType = (typeof CREDIT_TRANSACTION_TYPES)[number];

/**
 * Gelijk aan `CreditTransactionType::label()` in de API. Een regel draagt haar
 * eigen `type_label`; deze lijst is er voor het filter.
 */
export const CREDIT_TRANSACTION_LABELS: Record<CreditTransactionType, string> = {
    topup: 'Opwaardering',
    reservation: 'Reservering',
    consumption: 'Verbruik',
    release: 'Vrijgave',
    refund: 'Terugbetaling',
    adjustment: 'Correctie',
    bonus: 'Bonus',
    expiry: 'Vervallen',
};

/** `CreditTransaction::REFERENCE_TABLES` in de API: waar een boeking op kan staan. */
export const CREDIT_REFERENCE_TYPES = ['order', 'payment', 'invoice', 'tenant'] as const;

export type CreditReferenceType = (typeof CREDIT_REFERENCE_TYPES)[number];

export interface LedgerEntry {
    uuid: string;
    /** `null` voor een type dat de app niet kent; `typeLabel` komt altijd uit de API. */
    type: CreditTransactionType | null;
    typeLabel: string;
    /** Getekend: `-4` bij een reservering, `50` bij een opwaardering. */
    credits: number;
    balanceAfter: number;
    description: string | null;
    referenceType: CreditReferenceType | null;
    referenceUuid: string | null;
    /** `null` bij een automatische boeking. */
    authorName: string | null;
    createdAt: string | null;
}

function oneOf<T extends string>(values: readonly T[], value: string | undefined): T | null {
    return value !== undefined && (values as readonly string[]).includes(value)
        ? (value as T)
        : null;
}

export function toLedgerEntry(resource: CreditTransactionResource): LedgerEntry {
    return {
        uuid: resource.uuid,
        type: oneOf(CREDIT_TRANSACTION_TYPES, resource.type),
        typeLabel: resource.type_label,
        credits: resource.amount,
        balanceAfter: resource.balance_after,
        description: resource.description,
        referenceType: oneOf(CREDIT_REFERENCE_TYPES, resource.reference?.type),
        referenceUuid: resource.reference?.uuid ?? null,
        authorName: resource.author?.name ?? null,
        createdAt: resource.created_at,
    };
}

/** De filters die `CreditTransactionController` toestaat, en niets anders. */
export interface LedgerFilters {
    type?: CreditTransactionType | '';
    referenceType?: CreditReferenceType | '';
    referenceUuid?: string;
    /** `YYYY-MM-DD`, vanaf het begin van die dag. */
    from?: string;
    /** `YYYY-MM-DD`, tot en met die dag. */
    to?: string;
    page?: number;
    perPage?: number;
}

/* -------------------------------------------------------------------------- */
/* Opwaarderen                                                                 */
/* -------------------------------------------------------------------------- */

export interface CreditPackage {
    uuid: string;
    name: string;
    credits: number;
    /** Exclusief btw, `"450.00"`. */
    price: string;
    priceInclVat: string;
    vatRate: string;
}

export function toCreditPackage(resource: CreditPackageResource): CreditPackage {
    return {
        uuid: resource.uuid,
        name: resource.name,
        credits: resource.credits,
        price: resource.price,
        priceInclVat: resource.price_incl_vat,
        vatRate: resource.vat_rate,
    };
}

/**
 * Waar een betaling bij de betaalprovider staat (`PaymentStatus` in de API).
 * Alleen `paid` boekt credits; `open` kan nog alle kanten op.
 */
export const PAYMENT_STATUSES = ['open', 'paid', 'failed', 'cancelled', 'expired'] as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

function paymentStatusOf(value: string): PaymentStatus {
    return (PAYMENT_STATUSES as readonly string[]).includes(value)
        ? (value as PaymentStatus)
        : 'open';
}

/** Wat een betaling betaalt (`PaymentPurpose` in de API). */
export const PAYMENT_PURPOSES = [
    'subscription',
    'subscription_renewal',
    'extra_inspections',
    'single_reports',
    'credit_package',
] as const;

export type PaymentPurpose = (typeof PAYMENT_PURPOSES)[number];

/** De factuur van Smartkeur bij een betaalde betaling. */
export interface PaymentInvoice {
    uuid: string;
    number: string;
    issuedAt: string;
    /** De PDF is gemaakt en te downloaden. */
    pdfReady: boolean;
}

/**
 * Een betaling aan Smartkeur bij de betaalprovider: een abonnementsmaand,
 * keuringen, of een tegoedpakket van vroeger. Er is niets geboekt of geactiveerd
 * zolang de status niet `paid` is.
 */
export interface TopUpPayment {
    uuid: string;
    /** `null` voor een doel dat de app niet kent. */
    purpose: PaymentPurpose | null;
    description: string | null;
    /** Een onbekende status telt als `open`: nooit "geslaagd" of "mislukt" beweren zonder grond. */
    status: PaymentStatus;
    statusLabel: string;
    credits: number;
    /** Incl. btw, `"180.29"`. */
    amount: string;
    amountExclVat: string | null;
    vat: string | null;
    quantity: number | null;
    planName: string | null;
    periodStart: string | null;
    periodEnd: string | null;
    createdAt: string | null;
    /** Waar de gebruiker betaalt; `null` als de provider geen betaalpagina gaf. */
    checkoutUrl: string | null;
    expiresAt: string | null;
    paidAt: string | null;
    invoice: PaymentInvoice | null;
}

/** Elke betaling aan Smartkeur, niet alleen een opwaardering. */
export type Payment = TopUpPayment;

export function toTopUpPayment(resource: PaymentResource): TopUpPayment {
    return {
        uuid: resource.uuid,
        purpose: oneOf(PAYMENT_PURPOSES, resource.purpose),
        description: resource.description ?? null,
        status: paymentStatusOf(resource.status),
        statusLabel: resource.status_label,
        credits: resource.credits,
        amount: resource.amount,
        amountExclVat: resource.amount_excl_vat ?? null,
        vat: resource.vat ?? null,
        quantity: resource.quantity ?? null,
        planName: resource.plan?.name ?? null,
        periodStart: resource.period_start ?? null,
        periodEnd: resource.period_end ?? null,
        createdAt: resource.created_at ?? null,
        checkoutUrl: resource.checkout_url,
        expiresAt: resource.expires_at,
        paidAt: resource.paid_at,
        invoice: resource.invoice
            ? {
                  uuid: resource.invoice.uuid,
                  number: resource.invoice.number,
                  issuedAt: resource.invoice.issued_at,
                  pdfReady: resource.invoice.pdf_ready,
              }
            : null,
    };
}

/* -------------------------------------------------------------------------- */
/* Abonnement bij Smartkeur                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Hoe het abonnement ervoor staat: geen (proef of afgelopen), actief, opgezegd
 * (loopt tot `endsAt`) of met een mislukte incasso.
 */
export const SUBSCRIPTION_STATES = ['none', 'active', 'cancelling', 'past_due'] as const;

export type SubscriptionState = (typeof SUBSCRIPTION_STATES)[number];

export interface PlanSummary {
    code: string;
    name: string;
    /** Excl. btw, in hele centen. */
    monthlyPriceCents: number;
    includedInspections: number;
}

/**
 * Hoe de maanden na de eerste betaald worden: automatische incasso op een machtiging,
 * of elke maand een betaallink (iDEAL) als Mollie geen machtiging kan geven.
 */
export type SubscriptionBillingMethod = 'recurring' | 'link';

export interface SubscriptionOverview {
    state: SubscriptionState;
    stateLabel: string;
    subscription: {
        uuid: string;
        billingMethod: SubscriptionBillingMethod;
        plan: PlanSummary;
        /** Het abonnement vanaf de volgende abonnementsmaand, als er gewisseld is. */
        nextPlan: PlanSummary | null;
        currentPeriodStart: string | null;
        currentPeriodEnd: string | null;
        endsAt: string | null;
        pastDueSince: string | null;
    } | null;
    /** De volgende afschrijving, incl. btw; `null` als er geen volgt (opgezegd, achterstand). */
    nextCharge: { amount: string; amountExclVat: string; date: string | null } | null;
    included: { perMonth: number; left: number; expiresAt: string | null };
    purchasedLeft: number;
    purchase: {
        kind: 'extra_inspections' | 'single_reports';
        name: string;
        unitPriceCents: number;
        vatPercent: number;
        minQuantity: number;
        maxQuantity: number;
    };
    planPreference: string | null;
    /**
     * De betaallink van de komende maand (alleen bij `billingMethod` `link`): de ondertekende
     * betaalroute (terug naar Facturatie), wanneer de link per mail komt, en of hij nu open staat.
     */
    paymentLink: { url: string; period: string; sendsOn: string; due: boolean } | null;
    /** `meta.payments_test_mode`: dit bedrijf betaalt in productie in de testmodus van Mollie. */
    paymentsTestMode: boolean;
}

function toPlanSummary(resource: {
    code: string;
    name: string;
    monthly_price_cents: number;
    included_inspections: number;
}): PlanSummary {
    return {
        code: resource.code,
        name: resource.name,
        monthlyPriceCents: resource.monthly_price_cents,
        includedInspections: resource.included_inspections,
    };
}

export function toSubscriptionOverview(
    resource: SubscriptionOverviewResource,
    paymentsTestMode = false,
): SubscriptionOverview {
    const subscription = resource.subscription;

    return {
        state: oneOf(SUBSCRIPTION_STATES, resource.state) ?? 'none',
        stateLabel: resource.state_label,
        subscription: subscription
            ? {
                  uuid: subscription.uuid,
                  billingMethod: subscription.billing_method === 'link' ? 'link' : 'recurring',
                  plan: toPlanSummary(subscription.plan),
                  nextPlan: subscription.next_plan ? toPlanSummary(subscription.next_plan) : null,
                  currentPeriodStart: subscription.current_period_start,
                  currentPeriodEnd: subscription.current_period_end,
                  endsAt: subscription.ends_at,
                  pastDueSince: subscription.past_due_since,
              }
            : null,
        nextCharge: resource.next_charge
            ? {
                  amount: resource.next_charge.amount,
                  amountExclVat: resource.next_charge.amount_excl_vat,
                  date: resource.next_charge.date,
              }
            : null,
        included: {
            perMonth: resource.included.per_month,
            left: resource.included.left,
            expiresAt: resource.included.expires_at,
        },
        purchasedLeft: resource.purchased_left,
        purchase: {
            kind:
                resource.purchase.kind === 'extra_inspections'
                    ? 'extra_inspections'
                    : 'single_reports',
            name: resource.purchase.name,
            unitPriceCents: resource.purchase.unit_price_cents,
            vatPercent: resource.purchase.vat_percent,
            minQuantity: resource.purchase.min_quantity,
            maxQuantity: resource.purchase.max_quantity,
        },
        planPreference: resource.plan_preference,
        paymentLink: resource.payment_link
            ? {
                  url: resource.payment_link.url,
                  period: resource.payment_link.period,
                  sendsOn: resource.payment_link.sends_on,
                  due: resource.payment_link.due,
              }
            : null,
        paymentsTestMode,
    };
}

/** Een tijdelijke link naar de PDF van een factuur van Smartkeur. */
export interface InvoiceLink {
    url: string;
    filename: string;
}
